import PropTypes from "prop-types";
import { InputText } from "primereact/inputtext";

import Label from "../Label";

/**
 * A number input field with an optional label, helper text, error message, and suffix addon.
 *
 * @param {object} props
 * @param {string} props.id - Unique identifier for the input; used to associate the label and ARIA descriptions.
 * @param {string} [props.label] - Text displayed as the input label.
 * @param {string} [props.name] - HTML name attribute for the input element.
 * @param {number} [props.value] - The current value; must be provided for controlled usage.
 * @param {boolean} [props.isRequired=false] - When true, renders a required indicator on the label and sets `aria-required` on the input.
 * @param {string} [props.placeholder] - Placeholder text shown when the input is empty.
 * @param {function} [props.onChange] - Called with a DOM-compatible event `{ target: { name, value } }` when the value changes.
 * @param {string} [props.error] - Error message displayed below the input; also marks the input as invalid.
 * @param {string} [props.helperText] - Hint text shown below the input when there is no error.
 * @param {string} [props.suffixText] - Text rendered as an input group addon after the input field (e.g. a unit like "kg").
 * @param {string} [props.className] - Additional CSS classes applied to the input group wrapper.
 * @param {boolean} [props.useGrouping=true] - When true, formats the number with thousand separators.
 */
const InputNumber = ({
  id,
  label,
  name,
  value,
  isRequired,
  onChange,
  error,
  placeholder,
  helperText,
  suffixText,
  className,
  useGrouping = true,
}) => {
  const labelId = `${id}-label`;
  const errorId = `${id}-error`;
  const helperId = `${id}-helper`;
  const describedBy =
    [error ? errorId : null, !error && helperText ? helperId : null]
      .filter(Boolean)
      .join(" ") || undefined;

  const rawStr = value !== null && value !== undefined ? String(value) : "";

  let displayValue = rawStr;

  if (useGrouping && rawStr !== "") {
    displayValue = new Intl.NumberFormat("en-US").format(value);
  }

  const handleOnChange = (e) => {
    const regex = /^[0-9\b]+$/;

    const raw = e.target.value.replaceAll(",", "");

    if (raw === "" || regex.test(raw)) {
      onChange({
        target: { name, value: raw.trim() === "" ? null : Number(raw) },
      });
    }
  };

  return (
    <section aria-labelledby={labelId} className="flex flex-col gap-1 w-full">
      <div id={labelId}>
        <Label htmlFor={id} text={label} isRequired={!!isRequired} />
      </div>
      <div className={`p-inputgroup ${className || ""}`.trim()}>
        {/* InputText is used due to https://github.com/primefaces/primereact/issues/6324 */}
        <InputText
          id={id}
          aria-labelledby={labelId}
          aria-required={isRequired || undefined}
          aria-invalid={!!error}
          aria-describedby={describedBy}
          name={name}
          value={displayValue}
          placeholder={placeholder}
          onChange={handleOnChange}
          className={`!h-5${error ? " p-invalid" : ""}`}
        />
        {!!suffixText && (
          <span className="p-inputgroup-addon !py-0">{suffixText}</span>
        )}
      </div>
      {!!helperText && !error && (
        <small id={helperId} className="-mt-1 text-gray-500">
          {helperText}
        </small>
      )}
      {!!error && (
        <small id={errorId} role="alert" className="-mt-1 text-red-500">
          {error}
        </small>
      )}
    </section>
  );
};

InputNumber.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.number,
  isRequired: PropTypes.bool,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  error: PropTypes.string,
  helperText: PropTypes.string,
  suffixText: PropTypes.string,
  className: PropTypes.string,
  useGrouping: PropTypes.bool,
};

export default InputNumber;
